import React from 'react';
import { getPublicImageUrl } from '../config/storage';
import { Factory, Shield } from 'lucide-react';

interface IndustryBannerProps {
  selectedIndustry: string | null;
  selectedIndustriesCount?: number;
  totalProductsCount?: number;
}

const industryTitles: Record<string, { title: string; subtitle: string }> = {
  'MINERIA': { 
    title: 'Equipamiento para Minería', 
    subtitle: 'Protección extrema contra impactos, polvo, abrasión y condiciones severas' 
  },
  'CONSTRUCCION': { 
    title: 'Seguridad en Construcción', 
    subtitle: 'Cascos, arneses de altura, calzado reforzado y protección integral en obra' 
  },
  'MANUFACTURA': { 
    title: 'Protección para Manufactura', 
    subtitle: 'Guantes de precisión, protección auditiva, respiradores y vestimenta industrial' 
  },
  'PETROLERA': { 
    title: 'Industria Petrolera y Gas', 
    subtitle: 'Prendas ignífugas, botas para hidrocarburos y protección respiratoria avanzada' 
  },
  'QUIMICA': { 
    title: 'Seguridad Química y Biológica', 
    subtitle: 'Trajes de contención química, máscaras panorámicas y guantes especializados' 
  },
  'METALMECANICA': { 
    title: 'Sector Metalmecánico', 
    subtitle: 'Caretas para soldar, delantales de cuero, protección térmica y visual' 
  },
  'LOGISTICA': { 
    title: 'Logística y Depósitos', 
    subtitle: 'Fajas lumbares, calzado ergonómico antideslizante y chalecos reflectivos' 
  },
  'SALUD': { 
    title: 'Salud y Laboratorios', 
    subtitle: 'Guantes descartables, cofias, barbijos y protección biológica' 
  },
  'AGRICOLA': { 
    title: 'Seguridad Agrícola y Forestal', 
    subtitle: 'Protección contra pesticidas, polvos orgánicos, botas de goma y protección solar' 
  },
  'ALIMENTICIA': { 
    title: 'Industria Alimenticia', 
    subtitle: 'Higiene, calzado sanitario, cofias, delantales y guantes certificados' 
  }
};

const IndustryBanner: React.FC<IndustryBannerProps> = ({ 
  selectedIndustry, 
  selectedIndustriesCount = 0,
  totalProductsCount
}) => {
  const industryKey = selectedIndustry?.toUpperCase();
  const industryData = industryKey ? industryTitles[industryKey] : null;

  const title = industryData 
    ? industryData.title 
    : selectedIndustriesCount > 1 
      ? `Filtro: ${selectedIndustriesCount} Industrias Seleccionadas` 
      : 'Catálogo de Seguridad Industrial';

  const subtitle = industryData 
    ? industryData.subtitle 
    : 'Equipamiento de Protección Personal (EPP) certificado bajo normas internacionales';

  const bannerImage = getPublicImageUrl('banners/default-banner.jpeg', true);

  return (
    <div className="relative w-full rounded-2xl overflow-hidden shadow-lg border border-slate-700/30 bg-gradient-to-r from-slate-900 via-slate-800 to-amber-950 p-6 md:p-8 mb-4">
      {/* Background Image with Ambient Glow */}
      <img
        src={bannerImage}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-35"
        onError={(e) => {
          // Si no existe la imagen remota, dejar el degradado puro
          e.currentTarget.style.display = 'none';
        }}
      />
      
      {/* Dynamic Content */}
      <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="max-w-2xl">
          {industryKey && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">
              <Shield className="w-3.5 h-3.5" />
              Sector {industryKey}
            </div>
          )}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
            {title}
          </h1>
          <p className="mt-1.5 text-slate-300 text-sm md:text-base leading-relaxed">
            {subtitle}
          </p>
        </div>

        {totalProductsCount !== undefined && (
          <div className="shrink-0 flex md:flex-col items-center md:items-end justify-between border-t md:border-t-0 md:border-l border-slate-700/60 pt-3 md:pt-0 md:pl-6">
            <span className="text-xs text-slate-400 uppercase tracking-wider">Disponibles</span>
            <span className="text-2xl md:text-3xl font-black text-amber-400">
              {totalProductsCount}
            </span>
            <span className="text-xs text-slate-400">productos</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndustryBanner;












